import React from 'react';

import { maxWidthSm } from '../../../constants';
import { Arrow } from './Arrow';
import { Tab } from './Tab';
import { HotspotTab } from '@ael/core-api';
import * as Immutable from 'immutable';
import Slider, { ResponsiveObject } from 'react-slick';

import './TabsSlider.scss';

const SLICK_ANIMATION_SPEED = 300;
const SLICK_RESPONSIVENESS: ResponsiveObject[] = [
  {
    breakpoint: maxWidthSm,
    settings: {
      slidesToShow: 1,
    },
  },
];

interface TabsSliderProps {
  tabs: Immutable.List<HotspotTab>;
  maxVisibleTabs: number;
  selectedTabIndex: number;
  onTabSelect: (index: number) => void;
}

export const TabsSlider: React.FC<TabsSliderProps> = ({ selectedTabIndex, tabs, maxVisibleTabs, onTabSelect }) => {
  const sliderRef = React.useRef<Slider>();

  const [isAnimating, setIsAnimating] = React.useState(false);

  // this one prevents from going out of visible window (if you click fast enough without reacting to onAnimationChange)
  // you can force tab to be selected "out of screen"
  const triggerAnimationNotification = () => {
    setIsAnimating(true);

    const timeout = setTimeout(() => {
      setIsAnimating(false);
    }, SLICK_ANIMATION_SPEED);

    return () => clearTimeout(timeout);
  };

  // this few lines show the most vicious magic possible.
  // must access slicks state, to find current active breakpoint to have best guess for visible slides
  // with that guess we can calculate visible tab "moving window" and adjust it
  const slickVisibleSlides =
    SLICK_RESPONSIVENESS.find(
      // @ts-expect-error accessing state which is type never, but slicks used breakpoint is only there
      (responsiveObject) => responsiveObject.breakpoint === sliderRef.current?.state.breakpoint
      // @ts-expect-error settings might be as well string "unslick" and i do not want bother with it now
    )?.settings.slidesToShow ?? maxVisibleTabs;

  const visibleWindow = Math.min(slickVisibleSlides, maxVisibleTabs);
  const maxVisibleTabsHalf = Math.ceil(visibleWindow / 2);

  React.useEffect(() => {
    const updateViewBox = (selectedIndex: number) => {
      if (!sliderRef.current) {
        return;
      }

      if (visibleWindow === 1) {
        sliderRef.current.slickGoTo(selectedIndex);
        triggerAnimationNotification();

        return;
      }

      const firstGoToSlideIndex = visibleWindow - Math.floor((visibleWindow - 1) / 2);
      const lastGoToSlideIndex = tabs.size - maxVisibleTabsHalf;

      const isBeforeSlickRange = selectedIndex < firstGoToSlideIndex;
      const isAfterSlickRange = selectedIndex > lastGoToSlideIndex;

      if (!isBeforeSlickRange && !isAfterSlickRange) {
        sliderRef.current.slickGoTo(selectedIndex);
        triggerAnimationNotification();

        return;
      }

      if (isBeforeSlickRange) {
        sliderRef.current.slickGoTo(firstGoToSlideIndex - 1);
        triggerAnimationNotification();

        return;
      }

      if (isAfterSlickRange) {
        sliderRef.current.slickGoTo(lastGoToSlideIndex);
        triggerAnimationNotification();

        return;
      }
    };

    updateViewBox(selectedTabIndex);
  }, [sliderRef, selectedTabIndex, maxVisibleTabs, visibleWindow, maxVisibleTabsHalf, tabs.size]);

  const goToNext = () => {
    const newSelectedTabIndex = selectedTabIndex + 1;
    if (newSelectedTabIndex < tabs.size && !isAnimating) {
      onTabSelect(newSelectedTabIndex);
    }
  };

  const goToPrevious = () => {
    const newSelectedTabIndex = selectedTabIndex - 1;
    if (newSelectedTabIndex >= 0 && !isAnimating) {
      onTabSelect(newSelectedTabIndex);
    }
  };

  const allTabsVisible = tabs.size <= visibleWindow;

  const prevTabsCount = Math.max(selectedTabIndex - (visibleWindow > 1 ? Math.floor(visibleWindow / 2) : 0), 0);
  const nextTabsCount = Math.max(tabs.size - maxVisibleTabsHalf - selectedTabIndex, 0);

  return (
    <Slider
      className="tabs-bar__slider"
      slidesToShow={Math.min(tabs.size, maxVisibleTabs)}
      slidesToScroll={1}
      infinite={false}
      cssEase="ease-in-out"
      prevArrow={<Arrow furtherTabsCount={prevTabsCount} onArrowClick={goToPrevious} />}
      nextArrow={<Arrow furtherTabsCount={nextTabsCount} onArrowClick={goToNext} isNext />}
      arrows={!allTabsVisible}
      centerPadding="44px"
      speed={SLICK_ANIMATION_SPEED}
      centerMode={visibleWindow !== 1}
      responsive={SLICK_RESPONSIVENESS}
      swipeToSlide={visibleWindow !== 1}
      ref={(slider) => {
        if (slider) {
          sliderRef.current = slider;
        }
      }}
      afterChange={(currentIndex) => {
        if (visibleWindow === 1 && currentIndex !== selectedTabIndex) {
          onTabSelect(currentIndex);
        }
      }}
    >
      {tabs.toArray().map((tab, index) => (
        <Tab
          id={tab.id}
          key={tab.id}
          value={tab.title}
          selected={index === selectedTabIndex}
          onClick={() => onTabSelect(index)}
          hideLabel={tabs.size === 1}
        />
      ))}
      {/* remove after this is fixed https://github.com/akiran/react-slick/pull/1232 */}
      {!allTabsVisible &&
        Array(maxVisibleTabsHalf)
          .fill(0)
          .map((_, index: number) => <div key={`placeholder-${index}`} />)}
    </Slider>
  );
};
